import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { FaArrowLeft } from 'react-icons/fa6';
import Preloader from './Preloader';

// Responsive styles for SOSDetail
const responsiveStyles = `
@media (max-width: 900px) {
  .sosdetail-flex-row {
    flex-direction: column !important;
    gap: 0 !important;
  }
  .sosdetail-flex-row > div {
    width: 100% !important;
    margin-bottom: 24px;
  }
  .sosdetail-household-row {
    flex-direction: column !important;
    gap: 0 !important;
  }
  .sosdetail-household-row > div {
    width: 100% !important;
    margin-bottom: 24px;
  }
}
`;

const libraries = ['places', 'marker'];

function SOSDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [beacon, setBeacon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requester, setRequester] = useState(null);
  const [responder, setResponder] = useState(null);
  const [responderShelter, setResponderShelter] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const chatEndRef = useRef(null);
  const [requesterHousehold, setRequesterHousehold] = useState(null);
  const [responderHousehold, setResponderHousehold] = useState(null);
  const auditLoggedRef = useRef(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const mapContainerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '8px'
  };

  // Modern 2020 Google Maps style
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: true,
    scaleControl: true,
    streetViewControl: true,
    rotateControl: true,
    fullscreenControl: true,
    mapId: 'DEMO_MAP_ID',
    // Modern 2020 style - cleaner, lighter appearance
    styles: [
      {
        "elementType": "geometry",
        "stylers": [{ "color": "#f5f5f5" }]
      },
      {
        "elementType": "labels.icon",
        "stylers": [{ "visibility": "on" }]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#616161" }]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{ "color": "#f5f5f5" }]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#bdbdbd" }]
      },
      {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [{ "color": "#eeeeee" }]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#757575" }]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [{ "color": "#e5e5e5" }]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#9e9e9e" }]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{ "color": "#ffffff" }]
      },
      {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#757575" }]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{ "color": "#dadada" }]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#616161" }]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#9e9e9e" }]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [{ "color": "#e5e5e5" }]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [{ "color": "#eeeeee" }]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [{ "color": "#c9c9c9" }]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#9e9e9e" }]
      }
    ]
  }), []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA',
    libraries,
    // Force latest version
    version: 'weekly'
  });

  useEffect(() => {
    fetchSOSDetail();
    fetchCurrentUser();
    fetchChatMessages();
    // Subscribe to chat message changes
    const chatChannel = supabase
      .channel('public:chat_messages')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'chat_messages', filter: `sos_beacon_id=eq.${id}` },
        (payload) => {
          fetchChatMessages();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(chatChannel);
    };
  }, [id]);

  // Add audit log for viewing the SOS beacon detail (admin/super admin only)
  useEffect(() => {
    if (!auditLoggedRef.current && currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && id) {
      auditLoggedRef.current = true;
      (async () => {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'view sos beacon',
          target_id: id,
          target_type: 'SOSBeacon',
          details: {}
        }]);
      })();
    }
  }, [currentUser, id]);

  useEffect(() => {
      document.title = "SOS Beacon Details";
    }, []);

  useEffect(() => {
    // Scroll to bottom of chat when messages change
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  async function fetchSOSDetail() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sos_beacons')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      setBeacon(data);
      if (data.user_id) fetchProfile(data.user_id, setRequester);
      if (data.responder_id) fetchProfile(data.responder_id, setResponder, true);
      if (data.latitude && data.longitude) {
        setMapCenter({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) });
      }
      // Fetch households
      if (data.user_id && data.responder_id && data.user_id === data.responder_id) {
        // Same user, fetch once and set both
        fetchHousehold(data.user_id, (household) => {
          setRequesterHousehold(household);
          setResponderHousehold(household);
        });
      } else {
        if (data.user_id) fetchHousehold(data.user_id, setRequesterHousehold);
        if (data.responder_id) fetchHousehold(data.responder_id, setResponderHousehold);
      }
    } catch (error) {
      setBeacon(null);
    } finally {
      setLoading(false);
    }
  }

  async function fetchProfile(userId, setter, isResponder = false) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, user_id')
      .eq('user_id', userId)
      .single();
    if (!error && data) setter(data);
    if (!error && data) setProfiles(prev => ({ ...prev, [userId]: data }));
    if (!error && data && isResponder) {
      // Fetch temp shelter where shelter_provider matches profile.id
      const { data: shelter } = await supabase
        .from('temp_shelter')
        .select('*')
        .eq('shelter_provider', data.id)
        .single();
      if (shelter) setResponderShelter(shelter);
    }
  }

  async function fetchCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      setCurrentUser(profile);
    }
  }

  async function fetchChatMessages() {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('sos_beacon_id', id)
      .order('created_at', { ascending: true });
    if (!error && data) {
      setChatMessages(data);
      // Fetch all unique sender profiles
      const senderIds = Array.from(new Set(data.map(msg => msg.sender_id)));
      if (senderIds.length > 0) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('user_id,first_name,last_name')
          .in('user_id', senderIds);
        if (!profileError && profileData) {
          const map = {};
          profileData.forEach(profile => {
            map[profile.user_id] = profile;
          });
          setProfiles(prev => ({ ...prev, ...map }));
        }
      }
    }
  }

  async function handleSendMessage(e) {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser) return;
    
    // Additional security check: only admins can send messages
    if (currentUser.role !== 'admin' && currentUser.role !== 'super admin') {
      alert('Only administrators can send messages in this conversation.');
      return;
    }
    
    await supabase.from('chat_messages').insert([
      {
        sos_beacon_id: id,
        sender_id: currentUser.user_id,
        message: newMessage.trim(),
        created_at: new Date().toISOString()
      }
    ]);
    // Audit log for sent message
    await supabase.from('audit_logs').insert([{
      user_id: currentUser.id,
      action: 'sent message',
      target_id: id,
      target_type: 'SOSBeacon',
      details: { message: newMessage.trim() }
    }]);
    setNewMessage('');
    fetchChatMessages();
  }

  function getFullName(profile) {
    if (!profile) return '-';
    return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  }

  // Helper function to determine if current user is an admin participant
  function isAdminParticipant() {
    if (!currentUser || !beacon) return false;
    return currentUser.role === 'admin' || currentUser.role === 'super admin';
  }

  // Helper function to determine if message should be aligned to the right
  function shouldAlignRight(msgSenderId) {
    if (!currentUser) return false;
    
    // If current user is the sender, align right
    if (msgSenderId === currentUser.user_id) return true;
    
    return false;
  }

  // Helper function to get display name with admin suffix
  function getDisplayName(profile, msgSenderId) {
    if (!profile) return '-';
    
    const fullName = getFullName(profile);
    
    // If this is the current user and they're an admin participant, add (Admin) suffix
    if (currentUser && 
        msgSenderId === currentUser.user_id && 
        isAdminParticipant() &&
        beacon.user_id !== currentUser.user_id && 
        beacon.responder_id !== currentUser.user_id) {
      return `${fullName} (Admin)`;
    }
    
    return fullName;
  }

  // Helper function to get name color based on user role
  function getNameColor(profile, msgSenderId) {
    if (!profile) return '#08b1c7'; // Default color
    
    // If this is the current user and they're an admin participant, use role-specific colors
    if (currentUser && 
        msgSenderId === currentUser.user_id && 
        isAdminParticipant() &&
        beacon.user_id !== currentUser.user_id && 
        beacon.responder_id !== currentUser.user_id) {
      
      if (currentUser.role === 'super admin') {
        return '#e74c3c'; // Red for super admin
      } else if (currentUser.role === 'admin') {
        return '#f39c12'; // Orange for admin
      }
    }
    
    return '#08b1c7'; // Default light blue
  }

  // Create custom markers with icons (no text)
  const createCustomMarker = (type, color) => {
    const size = 48;
    
    if (type === 'evacuee') {
      // Evacuee marker - Red with person icon
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <g filter="url(#shadow)">
            <!-- Outer circle -->
            <circle cx="24" cy="24" r="22" fill="${color}" stroke="#ffffff" stroke-width="3" />
            <circle cx="24" cy="16" r="4" fill="#ffffff" />
            <path d="M 16 26 Q 24 36 32 26" stroke="#ffffff" stroke-width="2" fill="none" />
          </g>
        </svg>`;
      
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: size, height: size },
        anchor: { x: 24, y: 24 }
      };
    } else {
      // Responder marker - Blue with house icon
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.3"/>
            </filter>
          </defs>
          <g filter="url(#shadow)">
            <!-- Outer circle -->
            <circle cx="24" cy="24" r="22" fill="${color}" stroke="#ffffff" stroke-width="3" />
            <!-- House icon -->
            <path d="M 16 18 L 24 12 L 32 18 L 32 28 L 16 28 Z" fill="#ffffff" />
            <rect x="20" y="22" width="8" height="6" fill="${color}" />
          </g>
        </svg>`;
      
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: size, height: size },
        anchor: { x: 24, y: 24 }
      };
    }
  };

  // Generate markers array
  const markers = useMemo(() => {
    const markerArray = [];
    
    // Evacuee marker (Red) - at beacon location
    if (beacon && beacon.latitude && beacon.longitude) {
      markerArray.push({
        position: { 
          lat: parseFloat(beacon.latitude), 
          lng: parseFloat(beacon.longitude) 
        },
        icon: createCustomMarker('evacuee', '#dc2626'),
        title: 'Evacuee - Needs Assistance',
        zIndex: 1000
      });
    }
    
    // Responder marker (Blue) - at shelter location
    if (responderShelter && responderShelter.latitude && responderShelter.longitude) {
      markerArray.push({
        position: { 
          lat: parseFloat(responderShelter.latitude), 
          lng: parseFloat(responderShelter.longitude) 
        },
        icon: createCustomMarker('responder', '#2563eb'),
        title: 'Responder Shelter - Providing Assistance',
        zIndex: 999
      });
    }
    
    return markerArray;
  }, [beacon, responderShelter]);

  // Calculate optimal map center and zoom
  const mapConfig = useMemo(() => {
    if (!beacon || !beacon.latitude || !beacon.longitude) {
      return { center: { lat: 0, lng: 0 }, zoom: 2 };
    }
    
    const center = { 
      lat: parseFloat(beacon.latitude), 
      lng: parseFloat(beacon.longitude) 
    };
    
    // If we have both evacuee and responder locations, adjust view to show both
    if (responderShelter && responderShelter.latitude && responderShelter.longitude) {
      const responderLat = parseFloat(responderShelter.latitude);
      const responderLng = parseFloat(responderShelter.longitude);
      
      // Calculate bounds that include both points
      const bounds = {
        north: Math.max(center.lat, responderLat),
        south: Math.min(center.lat, responderLat),
        east: Math.max(center.lng, responderLng),
        west: Math.min(center.lng, responderLng)
      };
      
      return { 
        center,
        bounds,
        hasMultiplePoints: true
      };
    }
    
    return { center, zoom: 15, hasMultiplePoints: false };
  }, [beacon, responderShelter]);

  async function fetchHousehold(userId, setter) {
    const { data, error } = await supabase
      .from('households')
      .select('id,user_id,members,created_at,updated_at')
      .eq('user_id', userId)
      .maybeSingle();
    if (!error && data) {
      if (typeof data.members === 'string') {
        try {
          data.members = JSON.parse(data.members);
        } catch (e) {
          data.members = [];
        }
      }
      if (!Array.isArray(data.members)) {
        data.members = [];
      }
      setter(data);
    } else {
      setter(null);
    }
  }

  if (loading) return <Preloader text="Loading Details..." />;
  if (!currentUser) return <Preloader text="Loading..." />;
  if (currentUser.role !== 'admin' && currentUser.role !== 'super admin') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }
  if (!beacon) return <div>SOS Beacon not found</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <button
              onClick={() => navigate('/sos-beacons')}
              style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <FaArrowLeft/>
            </button>
            <div className="title-text">SOS Beacon Details</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />
        
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001' }}>
          <h1 style={{ fontSize: 24, marginBottom: 24 }}>SOS Beacon Details</h1>
          
          {/* Map Legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#dc2626', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}></div>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Evacuee </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#2563eb', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}></div>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Responder</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
            <div style={{ flex: 1 }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapConfig.center}
                  zoom={mapConfig.hasMultiplePoints ? 12 : 15}
                  options={mapOptions}
                >
                  {markers.map((marker, idx) => (
                    <Marker
                      key={idx}
                      position={marker.position}
                      title={marker.title}
                      zIndex={marker.zIndex}
                      icon={marker.icon}
                    />
                  ))}
                </GoogleMap>
              ) : (
                <div style={{ height: 500, borderRadius: 8, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                  Loading modern map...
                </div>
              )}
            </div>
            {/* Description on the right */}
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Evacuee</label>
                <span>{getFullName(requester)}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Responder</label>
                <span>{getFullName(responder)}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Status</label>
                <span>{beacon.status}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Created At</label>
                <span>{new Date(beacon.created_at).toLocaleString()}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Activated At</label>
                <span>{beacon.activated_at ? new Date(beacon.activated_at).toLocaleString() : '-'}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Completed At</label>
                <span>{beacon.completed_at ? new Date(beacon.completed_at).toLocaleString() : '-'}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Latitude</label>
                <span>{beacon.latitude}</span>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Longitude</label>
                <span>{beacon.longitude}</span>
              </div>
            </div>
          </div>
          {/* Households Table Section */}
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 20, marginBottom: 12 }}>Household Information</h2>
            <div style={{ display: 'flex', gap: 32 }}>
              {/* Evacuee Household */}
              <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 2px 8px #0001', border: '1px solid #fee2e2', overflow: 'hidden' }}>
                <div style={{ background: '#fee2e2', padding: '12px 20px', fontWeight: 'bold', fontSize: 17, color: '#dc2626', borderBottom: '1px solid #fee2e2' }}>Evacuee Family</div>
                {requesterHousehold ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                    <thead>
                      <tr style={{ background: '#fee2e2', color: '#dc2626', fontWeight: 'bold', fontSize: 15 }}>
                        <th style={{ padding: 10, borderBottom: '1px solid #fee2e2' }}>Name</th>
                        <th style={{ padding: 10, borderBottom: '1px solid #fee2e2', textAlign: 'center' }}>Age</th>
                        <th style={{ padding: 10, borderBottom: '1px solid #fee2e2', textAlign: 'center' }}>Gender</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(requesterHousehold.members) && requesterHousehold.members.length > 0 ? (
                        requesterHousehold.members.map((member, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: 10 }}>{member.name}</td>
                            <td style={{ padding: 10, textAlign: 'center' }}>{member.age}</td>
                            <td style={{ padding: 10, textAlign: 'center' }}>{member.gender}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', padding: 16 }}>No members found</td></tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ color: '#888', padding: 16 }}>No household data found.</div>
                )}
              </div>
              {/* Responder Household */}
              <div style={{ flex: 1, background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 2px 8px #0001', border: '1px solid #dbeafe', overflow: 'hidden' }}>
                <div style={{ background: '#dbeafe', padding: '12px 20px', fontWeight: 'bold', fontSize: 17, color: '#2563eb', borderBottom: '1px solid #dbeafe' }}>Responder Family</div>
                {responderHousehold ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                    <thead>
                      <tr style={{ background: '#dbeafe', color: '#2563eb', fontWeight: 'bold', fontSize: 15 }}>
                        <th style={{ padding: 10, borderBottom: '1px solid #dbeafe' }}>Name</th>
                        <th style={{ padding: 10, borderBottom: '1px solid #dbeafe', textAlign: 'center' }}>Age</th>
                        <th style={{ padding: 10, borderBottom: '1px solid #dbeafe', textAlign: 'center' }}>Gender</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(responderHousehold.members) && responderHousehold.members.length > 0 ? (
                        responderHousehold.members.map((member, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: 10 }}>{member.name}</td>
                            <td style={{ padding: 10, textAlign: 'center' }}>{member.age}</td>
                            <td style={{ padding: 10, textAlign: 'center' }}>{member.gender}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', padding: 16 }}>No members found</td></tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ color: '#888', padding: 16 }}>No household data found.</div>
                )}
              </div>
            </div>
          </div>
          {/* Chat Section */}
          <div style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: 20, marginBottom: 12 }}>Conversation Chat</h2>
            <div style={{ maxHeight: 300, overflowY: 'auto', background: '#f4f8f8', borderRadius: 8, padding: 16, marginBottom: 12, border: '1px solid #eee' }}>
              {chatMessages.length === 0 && <div style={{ color: '#888', textAlign: 'center' }}>No messages yet.</div>}
              {chatMessages.map((msg, idx) => (
                <div key={msg.id || idx} style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', alignItems: shouldAlignRight(msg.sender_id) ? 'flex-end' : 'flex-start' }}>
                  <div style={{ fontWeight: 'bold', color: getNameColor(profiles[msg.sender_id], msg.sender_id), fontSize: 15 }}>{getDisplayName(profiles[msg.sender_id], msg.sender_id)}</div>
                  <div style={{ background: shouldAlignRight(msg.sender_id) ? '#19c37d' : '#fff', color: shouldAlignRight(msg.sender_id) ? '#fff' : '#333', borderRadius: 8, padding: '8px 14px', maxWidth: 320, wordBreak: 'break-word', marginTop: 2 }}>{msg.message}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{new Date(msg.created_at).toLocaleString()}</div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            {currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && (
              <div style={{ marginBottom: 8, color: '#fff', background: '#e74c3c', borderRadius: 6, padding: '8px 16px', fontWeight: 600, display: 'inline-block' }}>
                You are sending messages as <span style={{ fontWeight: 'bold' }}>{currentUser.role === 'super admin' ? 'Super Admin' : 'Admin'}</span>.
              </div>
            )}
            {currentUser && currentUser.role !== 'admin' && currentUser.role !== 'super admin' && (
              <div style={{ marginBottom: 8, color: '#fff', background: '#f39c12', borderRadius: 6, padding: '8px 16px', fontWeight: 600, display: 'inline-block' }}>
                Only administrators can send messages in this conversation.
              </div>
            )}
            <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder={currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') ? "Type your message..." : "Only admins can send messages"}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #ccc' }}
                disabled={!currentUser || currentUser.role !== 'admin' && currentUser.role !== 'super admin'}
              />
              <button 
                type="submit" 
                style={{ 
                  background: currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') ? '#19c37d' : '#ccc', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '0 18px', 
                  fontWeight: 'bold', 
                  fontSize: 16 
                }} 
                disabled={!currentUser || !newMessage.trim() || (currentUser.role !== 'admin' && currentUser.role !== 'super admin')}
              >
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SOSDetail;